import * as H from 'history'
import { useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useEffectOnce } from 'react-use'

type OnPageChangeCallback = (
  location?: H.LocationDescriptor,
  action?: string
) => void

// this custom hook is the core logic of the OnPageChange component
function useOnPageChange(onChange: OnPageChangeCallback) {
  const history = useHistory()
  const unlisten = useRef(null)

  useEffectOnce(() => {
    if (!unlisten.current) {
      unlisten.current = history.listen((location, action) => {
        typeof onChange === 'function' && onChange(location, action)
      })
    }

    return () => {
      if (unlisten.current) {
        unlisten.current()
      }
    }
  })
}

// this utility component is used here to ensure that the notifications menu closes when the page navigates
// this is needed because some navigation within the notifications (viewing running/complete scenarios)
// stays on the same higher level route, and routes within that page (to a diff scenario or run)
// and the menu does not end up getting closed as a result
// so this component listens for navigation changes and calls a handler when that happens
//
// this is a component in order to both a) isolate/compartmentalize this logic (which could be useful elsewhere later)
// and b) it has to be rendered as a child of the Popover function-as-children content in order to receive the
// closePopover function
export const OnPageChange: React.FC<{
  onChange: OnPageChangeCallback
}> = ({ onChange }) => {
  useOnPageChange(onChange)

  return null
}
